import { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { MdOutlineAddBox } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  CardBody,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ApplicationCard } from '../../../components/application-card/application-card.component';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { resetAppDetails } from '../../../redux/appBuilder/appDetails';
import { resetAppStepperDetails } from '../../../redux/appBuilder/stepperDetails';
import { useAppDispatch } from '../../../redux/hooks';
import { updateAppAnaytics } from '../../../redux/viewApp/appAnalytics';
import {
  getAllUserApplicationsWithPagination,
  getAllUserZkFetchApplicationsWithPagination,
} from '../../../utils/app-builder/fetch-applications';
import { useDebounce } from '../../../utils/logs/send-logs';
import { HomeDashboard } from '../../applications/components/dashboard.component';
import { getAllBlueCheckApplications } from '../../../utils/bluecheck-applications/fetch-bluecheck-applications';
import { ViewBlueCheckApps } from '../../../components/bluecheck-application-card/bluecheck-application-card.component';

export const ViewApps = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [myApplicationOptions, setMyApplicationOptions] = useState<any>([]);
  const debouncedSearchTerm = useDebounce(searchQuery, 500);
  const [totalMyApplications, setTotalMyApplications] = useState(10);
  const [selectedAppType, setSelectedAppType] = useState('WITNESS');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { width, height } = useWindowDimensions();
  const [noApplicationsFound, setNoApplicationsFound] = useState(false);
  const [appDeleted, setAppDeleted] = useState(false);
  const getPageSizeForScreen = () => {
    if (width < 1650 && width >= 1000) {
      return 6;
    } else if (width < 1696 && width >= 1650) {
      return 9;
    } else if (width < 2040 && width >= 1696) {
      return 12;
    } else if (width < 2300 && width >= 2040) {
      return 16;
    } else {
      return 16;
    }
  };

  const pageSize = getPageSizeForScreen();

  const fetchApplicationData = async (pageKey: number) => {
    let myApplications = null;

    if (selectedAppType === 'BC_APP') {
      try {
        const data = await getAllBlueCheckApplications();
        myApplications = data;
        if (myApplications.length === 0) {
          setNoApplicationsFound(true);
        } else {
          setNoApplicationsFound(false);
        }
        setMyApplicationOptions([...myApplications]);
        setTotalMyApplications(myApplications?.length);
        setTotalPages(Math.ceil(myApplications?.length / pageSize));
      } catch (err) {
        console.log(err);
      }
    } else {
      if (selectedAppType === 'WITNESS') {
        myApplications = await getAllUserApplicationsWithPagination(
          pageKey * pageSize,
          pageSize,
          searchQuery
        );
      } else {
        myApplications = await getAllUserZkFetchApplicationsWithPagination(
          pageKey * pageSize,
          pageSize,
          searchQuery
        );
        // add appType as ZK_FETCH if not present in appDetail while updateAppDetails
        myApplications?.applications.forEach((app: any) => {
          if (!app.appType) {
            app.appType = 'ZK_FETCH';
          }
        });
      }

      if (myApplications?.applications.length === 0) {
        setNoApplicationsFound(true);
      } else {
        setNoApplicationsFound(false);
      }

      setMyApplicationOptions([...myApplications?.applications]);
      setTotalMyApplications(myApplications?.count);
      setTotalPages(Math.ceil(myApplications?.count / pageSize));
    }
  };

  useEffect(() => {
    fetchApplicationData(currentPage - 1);
  }, [debouncedSearchTerm, currentPage, appDeleted]);

  useEffect(() => {
    // reset the current pages, total pages, debouncedSearchTerm, myApplications, totalMyApplications to initial state when the app type changes
    setCurrentPage(1);
    setTotalPages(1);
    fetchApplicationData(0);
  }, [selectedAppType]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          className="x-small-font"
          onClick={() => handlePageChange(i)}
          bgColor={currentPage === i ? 'whitesmoke' : 'white'}
        >
          {i}
        </Button>
      );
    }

    return pages;
  };

  const handleAppSelect = (appId: string) => {
    const selectedApp = myApplicationOptions.find((app: any) => app.appId === appId);
    dispatch(updateAppAnaytics({ appDetail: selectedApp }));
    // dispatch(updateViewAppActiveStep(1))
    navigate(`/my-applications/${appId}`);
  };

  const handleNewAppClick = () => {
    dispatch(resetAppDetails());
    dispatch(resetAppStepperDetails());
    navigate('/new-application');
  };

  return (
    <>
      <Box display="flex" h="fit-content" justifyContent="center" width="100%">
        <Card
          w={width > 1740 ? '1300px' : '95%'}
          mt={6}
          variant="outline"
          borderRadius="12px"
          h="fit-content"
        >
          <CardBody w="100%" p={0}>
            <VStack
              align="flex-start"
              padding="32px"
              w="100%"
              h={noApplicationsFound ? '80vh' : 'fit-content'}
              spacing="30px"
            >
              <VStack align="flex-start" w="100%" spacing="4px">
                <Text
                  fontFamily="inter"
                  className="medium-font"
                  fontWeight="bold"
                  color="black"
                >
                  My Applications
                </Text>
                <Text
                  color="gray"
                  className="small-font"
                  whiteSpace="pre-line"
                  fontFamily="inter"
                >
                  Applications you have built
                </Text>
                <HStack mt="14px" w="100%" justifyContent="space-between">
                  <HStack spacing="20px">
                    <InputGroup w="350px">
                      <InputLeftElement pointerEvents="none">
                        <FiSearch color="gray" style={{ marginBottom: 5 }} />
                      </InputLeftElement>
                      <Input
                        size="sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        type="search"
                        display="flex"
                        alignContent="center"
                        placeholder="Search"
                        className="small-font"
                        borderRadius="8px"
                        variant="filled"
                        bgColor="#f3f5fc"
                        py={5}
                        fontWeight={400}
                      />
                    </InputGroup>

                    <Button
                      leftIcon={<MdOutlineAddBox size={18} color="white" />}
                      className="small-font"
                      borderRadius="8px"
                      onClick={handleNewAppClick}
                      variant="outline"
                      bgColor="black"
                      _hover={{
                        bgColor: 'black',
                        transform: 'scale(1.1)',
                      }}
                      color="white"
                      size="md"
                      fontWeight={500}
                    >
                      Create New App
                    </Button>
                  </HStack>
                  <HStack mr={4} spacing={2}>
                    <Text className="x-small-font" fontFamily="inter" color="gray">
                      App Type:
                    </Text>
                    <Select
                      size="sm"
                      className="small-font"
                      borderRadius="8px"
                      bgColor="#f3f5fc"
                      fontWeight={400}
                      w="200px"
                      onChange={(e) => setSelectedAppType(e.target.value)}
                    >
                      <option value="WITNESS">Mobile & Web</option>
                      <option value="ZK_FETCH">zkFetch</option>
                      <option value="BC_APP">BlueCheck Apps</option>
                    </Select>
                  </HStack>
                </HStack>
              </VStack>
              {noApplicationsFound ? (
                <VStack w="100%" justifyContent="center">
                  <HomeDashboard heading="No Apps Found" showIllustration={false} />
                </VStack>
              ) : (
                <>
                  {myApplicationOptions.length === 0 && (
                    <Text className="small-font" fontFamily="inter">
                      Loading
                    </Text>
                  )}
                  <Grid
                    w="100%"
                    templateColumns="repeat(auto-fill,minmax(255px,1fr))"
                    gap={'14px'}
                  >
                    {selectedAppType === 'BC_APP' ? (
                      <ViewBlueCheckApps
                        applications={myApplicationOptions}
                        noApplicationsFound={noApplicationsFound}
                        loading={myApplicationOptions.length === 0}
                        setAppDeleted={setAppDeleted}
                      />
                    ) : (
                      myApplicationOptions.map((application: any) => (
                        <GridItem key={application.appId}>
                          <ApplicationCard
                            appDetail={application}
                            pointerCursor={selectedAppType === 'WITNESS'}
                            onSelect={
                              selectedAppType === 'WITNESS' ? handleAppSelect : undefined
                            }
                          />
                        </GridItem>
                      ))
                    )}
                  </Grid>
                  {selectedAppType !== 'BC_APP' && (
                    <HStack
                      w="100%"
                      padding="8px"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={4}
                    >
                      <Text fontFamily="inter" className="x-small-font">
                        Page {currentPage} of {totalPages}
                      </Text>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          bgColor="whitesmoke"
                          className="x-small-font"
                          onClick={() => handlePageChange(1)}
                          isDisabled={currentPage === 1}
                        >
                          <ArrowLeftIcon boxSize={3} />
                        </Button>
                        <Button
                          size="sm"
                          bgColor="whitesmoke"
                          className="x-small-font"
                          onClick={() => handlePageChange(currentPage - 1)}
                          isDisabled={currentPage === 1}
                        >
                          <ChevronLeftIcon boxSize={4} />
                        </Button>
                        {renderPageNumbers()}
                        <Button
                          size="sm"
                          bgColor="whitesmoke"
                          className="x-small-font"
                          onClick={() => handlePageChange(currentPage + 1)}
                          isDisabled={currentPage === totalPages}
                        >
                          <ChevronRightIcon boxSize={4} />
                        </Button>
                        <Button
                          size="sm"
                          bgColor="whitesmoke"
                          className="x-small-font"
                          onClick={() => handlePageChange(totalPages)}
                          isDisabled={currentPage === totalPages}
                        >
                          <ArrowRightIcon boxSize={3} />
                        </Button>
                      </HStack>
                    </HStack>
                  )}
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Box>
    </>
  );
};
